import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil';
import Spinner from '@/ui/content/Spinner';
import calendar from '@/public/images/icons/calendar.svg';
import caretLeft from '@/public/images/icons/caret-left.svg';
import { useLocalization } from '@/services/localizationService';
import { Price } from '@/services/priceService';
import { isBusySelector } from '@/stores/appStore';
import {
  onboardingState,
  offerPageIntroPriceIdState,
  offerPageVariantState,
  selectedPeriodState,
  showFreeTrialSelector,
} from '@/stores/onboardingStore';
import { usePriceService } from '@/layout/appWrapper/ServiceProvider';
import { FREE_TRIAL_DAYS, STRIPE_APPEARANCE } from '@/services/paymentService';
import { mediaShownInComparerState } from '@/stores/mediaStore';
import { useStripeProvider } from '@/core/stripe/useStripeProvider';
import { Elements } from '@stripe/react-stripe-js';
import { StripeElementsOptions } from '@stripe/stripe-js';
import { useUser } from '@/services/userService';
import settingsState from '@/stores/settingsStore';
import PaymentRatio from '../PaymentRatio';
import PaymentInformationForm from '../../PaymentInformationForm';

interface Props {
  selectedPrice: Price;
  onSubmit?: CallableFunction;
  onBackToOfferPage?: () => void;
}

export default function PaymentStrategiesStep2({
  selectedPrice,
  onSubmit,
  onBackToOfferPage,
}: Props) {
  const [onboarding, setOnboarding] = useRecoilState(onboardingState);
  const setOfferPageIntroPriceId = useSetRecoilState(
    offerPageIntroPriceIdState
  );
  const offerPageIntroPriceId = useRecoilValue(offerPageIntroPriceIdState);
  const offerPageVariant = useRecoilValue(offerPageVariantState);
  const { usePaywallPrices, useFormatRecurringPrice, usePricesByIds } =
    usePriceService();
  const paywallPrices = usePaywallPrices();
  const { t, data } = useLocalization();
  const isBusy = useRecoilValue(isBusySelector);
  const mediaInComparer = useRecoilValue(mediaShownInComparerState);
  const willDownloadAutomatically = Boolean(mediaInComparer.hasWatermark);

  const { data: user } = useUser();
  const settings = useRecoilValue(settingsState);

  const { selectedPeriod, selectedPaymentStrategy } = onboarding;
  const showFreeTrial = useRecoilValue(showFreeTrialSelector);
  const isOcpWelcomeOffer =
    offerPageVariant === 'ocp' && Boolean(offerPageIntroPriceId);
  const { data: introPrices } = usePricesByIds(
    offerPageIntroPriceId ? [offerPageIntroPriceId] : []
  );
  const introPrice = introPrices?.[0];

  const price = useFormatRecurringPrice(
    paywallPrices?.[selectedPaymentStrategy?.id || '']?.[selectedPeriod]
  );
  const welcomeOfferPrice =
    useFormatRecurringPrice(introPrice).split(/\/| every /)[0];
  const [amount, period] = price ? price.split('/') : ['', ''];
  const { stripePromise } = useStripeProvider();

  const resetSelectedPeriod = useResetRecoilState(selectedPeriodState);
  if (!paywallPrices || !user || (isOcpWelcomeOffer && !introPrice)) {
    return <Spinner inline={false} />;
  }

  const off =
    selectedPeriod === 'monthly' ? 35 : selectedPeriod === 'yearly' ? 50 : 0;

  const options: StripeElementsOptions = {
    mode: 'setup',
    currency: selectedPrice.currency,
  };

  const hasIntroPrice =
    offerPageVariant === 'pcp' || (offerPageVariant === 'ocp' && showFreeTrial)
      ? false
      : Boolean(offerPageIntroPriceId) ||
        (user.monetization.trialAvailable &&
          !settings.values.isFreeTrialEnabled &&
          settings.values.isIntroPriceEnabled);
  const isPcpOffer = offerPageVariant === 'pcp' && !showFreeTrial;
  const pcpOfferLabel =
    selectedPeriod === 'yearly' ? t(data.common.yearly) : t(data.common.weekly);
  const pcpOfferTerms = showFreeTrial ? (
    <>
      {t(data.common.paywallOfferPageFreeForDays, { days: 7 })},{' '}
      {t(data.common.then, null, 'lowercase')}{' '}
    </>
  ) : null;

  return (
    <div className="relative w-full bg-white text-black p-10">
      <div className="bg-white rounded-3xl border-2 border-gray-100 shadow-white-button overflow-hidden">
        <div className="p-6 pt-5 pb-4 flex justify-between items-center">
          <div className="flex items-center">
            <button
              type="button"
              className="p-2 -ms-2 me-2"
              aria-label={data.common.back}
              onClick={() => {
                if (onBackToOfferPage) {
                  onBackToOfferPage();
                  return;
                }

                setOnboarding((prev) => ({
                  ...prev,
                  selectedPaymentStrategy: null,
                }));
                setOfferPageIntroPriceId(null);
                resetSelectedPeriod();
              }}
              disabled={isBusy}
            >
              <img {...caretLeft} className="rtl:scale-x-[-1]" alt="" />
            </button>
            <div>
              {isPcpOffer ? (
                <>
                  <p className="SB16 mb-1 text-gray-400">
                    <span className="text-primary-accent capitalize">
                      {pcpOfferLabel}
                    </span>
                    {' · '}
                    <span className="M16">
                      {selectedPaymentStrategy?.title || ''}
                    </span>
                  </p>
                  <p className="M14 text-overlay-black-60">
                    {pcpOfferTerms}
                    <strong className="text-black">{price}</strong>
                  </p>
                </>
              ) : (
                <p className="SB16 mb-1 text-gray-400">
                  {showFreeTrial && (
                    <span>
                      <span className="text-primary-accent">
                        🎁{' '}
                        {t(data.onboarding.daysFreeAlt, { n: FREE_TRIAL_DAYS })}
                      </span>
                      {' · '}
                    </span>
                  )}
                  {hasIntroPrice && (
                    <span>
                      <span className="text-primary-accent">
                        🎁{' '}
                        {isOcpWelcomeOffer ? (
                          <span className="SB18 text-primary-accent ms-1">
                            <span>
                              {t(data.common.paywallOfferPageDays, {
                                days:
                                  (introPrice?.recurringIntervalCount || 2) * 7,
                              })}{' '}
                              {t(data.common.paywallOfferPageAtPrice, {
                                price: welcomeOfferPrice,
                              })}
                            </span>{' '}
                            <span className="mx-1">
                              {t(data.common.then, null, 'lowercase')}
                            </span>
                            <span>
                              {amount} <span className="lowercase">per</span>{' '}
                              {period}
                            </span>
                          </span>
                        ) : settings.values.compliantPriceEnabled ? (
                          <span className="SB18 text-primary-accent ms-1">
                            <span>{t(data.onboarding.daysIntro)}</span>
                            <span className="mx-1">
                              {t(data.common.then, null, 'lowercase')}
                            </span>
                            <span>
                              {amount} <span className="lowercase">per</span>{' '}
                              {period}
                            </span>
                          </span>
                        ) : (
                          <>
                            <span className="text-gray-400 line-through SB18">
                              {amount}
                            </span>
                            <span className="SB18 text-primary-accent ms-1">
                              {t(data.onboarding.daysIntro)}
                            </span>
                          </>
                        )}
                      </span>
                      {' · '}
                    </span>
                  )}
                  <span className="M16">
                    {selectedPaymentStrategy?.title || ''}
                  </span>
                </p>
              )}
              {!isPcpOffer && (
                <p>
                  {!hasIntroPrice && (
                    <PaymentRatio
                      price={price}
                      className="M14"
                      firstSpanClassName="SB20"
                      hasIntroPrice={hasIntroPrice}
                    />
                  )}
                </p>
              )}
            </div>
          </div>
          {!!off && (
            <p className="B10 ms-2 text-black text-center bg-secondary-yellow py-1 px-2 rounded-full">
              {t(data.onboarding.discount, { n: off }, 'uppercase')}
            </p>
          )}
        </div>
        {selectedPeriod === 'yearly' ? (
          <div className="bg-gray-100 bg-opacity-40 flex p-4 px-6">
            <img {...calendar} alt="" />
            <div className="ms-3 text-overlay-black-60">
              {hasIntroPrice ? (
                <p className="M12 whitespace-pre-line">
                  {t(data.onboarding.automaticRenewalTerms, {
                    formattedPrice: price,
                  })}
                </p>
              ) : (
                <>
                  <p className="B12">{t(data.onboarding.emailReminder)}:</p>
                  <p className="M12">
                    {showFreeTrial
                      ? t(data.onboarding.emailReminderSubtitleWithFT)
                      : t(data.onboarding.emailReminderSubtitle)}
                  </p>
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>
      <hr className="border-gray-100 my-8" />
      <p className="SB18 tracking-normal mb-4">
        {t(data.onboarding.paymentMethod)}
      </p>
      <Elements
        stripe={stripePromise}
        options={{
          ...options,
          appearance: STRIPE_APPEARANCE,
        }}
      >
        <PaymentInformationForm
          selectedPrice={selectedPrice}
          onSubmit={onSubmit || (() => {})}
          willDownloadAutomatically={willDownloadAutomatically}
          isIntroPriceActive={hasIntroPrice}
          introPriceId={offerPageIntroPriceId || undefined}
        />
      </Elements>
    </div>
  );
}
